import { z } from 'zod';
import { apiEnvelope } from '../envelope';
import { memberDetailChangeSchema } from './detail-changes';
import { COUNTRY_CODE_PATTERN, DEFAULT_COUNTRY_CODE } from './addresses';
import { e164PhoneSchema } from '../eligibility/schema';

/**
 * Which writer appended an emergency contact or clinic row. Closed vocabulary
 * today; new values need a migration (ADR-056 Rule 8).
 */
export const MEMBER_EMERGENCY_SOURCE = ['atlas_staff'] as const;
export const memberEmergencySourceSchema = z.enum(MEMBER_EMERGENCY_SOURCE);
export type MemberEmergencySource = z.infer<typeof memberEmergencySourceSchema>;

/** Bounds shared with the Drizzle CHECK constraints (ADR-104 / ADR-105). */
export const MEMBER_EMERGENCY_NAME_MAX_LENGTH = 200;
export const MEMBER_EMERGENCY_RELATIONSHIP_MAX_LENGTH = 120;
export const MEMBER_EMERGENCY_LINE_MAX_LENGTH = 200;
export const MEMBER_EMERGENCY_CITY_MAX_LENGTH = 120;
export const MEMBER_EMERGENCY_STATE_MAX_LENGTH = 60;
export const MEMBER_EMERGENCY_POSTAL_MAX_LENGTH = 16;
export const E164_PHONE_PATTERN = /^\+[1-9]\d{6,14}$/;

function isPresent(value: string | null): boolean {
  return value !== null && value.trim() !== '';
}

function hasEmergencyContactValue(body: {
  name: string | null;
  relationship: string | null;
  telephoneNumber: string | null;
}): boolean {
  return isPresent(body.name) || isPresent(body.relationship) || body.telephoneNumber !== null;
}

function hasEmergencyClinicValue(body: {
  name: string | null;
  line1: string | null;
  line2: string | null;
  line3: string | null;
  city: string | null;
  state: string | null;
  country: string | null;
  postalCode: string | null;
  telephoneNumber: string | null;
}): boolean {
  return (
    isPresent(body.name) ||
    isPresent(body.line1) ||
    isPresent(body.line2) ||
    isPresent(body.line3) ||
    isPresent(body.city) ||
    isPresent(body.state) ||
    body.country !== null ||
    isPresent(body.postalCode) ||
    body.telephoneNumber !== null
  );
}

/**
 * Staff append of a native emergency contact row. Every value field is
 * nullable; `currentId` is required so a stale baseline cannot be omitted.
 */
export const memberEmergencyContactWriteRequestSchema = z
  .object({
    name: z.string().trim().max(MEMBER_EMERGENCY_NAME_MAX_LENGTH).nullable(),
    relationship: z.string().trim().max(MEMBER_EMERGENCY_RELATIONSHIP_MAX_LENGTH).nullable(),
    telephoneNumber: e164PhoneSchema.nullable(),
    currentId: z.string().uuid().nullable(),
    change: memberDetailChangeSchema,
  })
  .strict()
  .refine(hasEmergencyContactValue, { message: 'nothing_to_record' });

export type MemberEmergencyContactWriteRequest = z.infer<
  typeof memberEmergencyContactWriteRequestSchema
>;

export const memberEmergencyClinicWriteRequestSchema = z
  .object({
    name: z.string().trim().max(MEMBER_EMERGENCY_NAME_MAX_LENGTH).nullable(),
    line1: z.string().trim().max(MEMBER_EMERGENCY_LINE_MAX_LENGTH).nullable(),
    line2: z.string().trim().max(MEMBER_EMERGENCY_LINE_MAX_LENGTH).nullable(),
    line3: z.string().trim().max(MEMBER_EMERGENCY_LINE_MAX_LENGTH).nullable(),
    city: z.string().trim().max(MEMBER_EMERGENCY_CITY_MAX_LENGTH).nullable(),
    state: z.string().trim().max(MEMBER_EMERGENCY_STATE_MAX_LENGTH).nullable(),
    postalCode: z.string().trim().max(MEMBER_EMERGENCY_POSTAL_MAX_LENGTH).nullable(),
    country: z
      .string()
      .trim()
      .regex(COUNTRY_CODE_PATTERN, 'must be an ISO 3166-1 alpha-2 country code')
      .nullable(),
    telephoneNumber: e164PhoneSchema.nullable(),
    currentId: z.string().uuid().nullable(),
    change: memberDetailChangeSchema,
  })
  .strict()
  .refine(hasEmergencyClinicValue, { message: 'nothing_to_record' });

export type MemberEmergencyClinicWriteRequest = z.infer<
  typeof memberEmergencyClinicWriteRequestSchema
>;

export const memberEmergencyContactRowSchema = z.object({
  id: z.string().uuid(),
  name: z.string().nullable(),
  relationship: z.string().nullable(),
  telephoneNumber: z.string().nullable(),
  createdAt: z.string(),
});

export type MemberEmergencyContactRowWire = z.infer<typeof memberEmergencyContactRowSchema>;

export const memberEmergencyClinicRowSchema = z.object({
  id: z.string().uuid(),
  name: z.string().nullable(),
  addressLine1: z.string().nullable(),
  addressLine2: z.string().nullable(),
  addressLine3: z.string().nullable(),
  city: z.string().nullable(),
  state: z.string().nullable(),
  country: z.string().nullable(),
  postalCode: z.string().nullable(),
  telephoneNumber: z.string().nullable(),
  createdAt: z.string(),
});

export type MemberEmergencyClinicRowWire = z.infer<typeof memberEmergencyClinicRowSchema>;

export const memberEmergencyContactWriteDataSchema = z.object({
  contact: memberEmergencyContactRowSchema,
});

export const memberEmergencyContactWriteResponseSchema = apiEnvelope(
  memberEmergencyContactWriteDataSchema,
);

export const memberEmergencyClinicWriteDataSchema = z.object({
  clinic: memberEmergencyClinicRowSchema,
});

export const memberEmergencyClinicWriteResponseSchema = apiEnvelope(
  memberEmergencyClinicWriteDataSchema,
);

export { DEFAULT_COUNTRY_CODE, COUNTRY_CODE_PATTERN };
